/**
 * Bulk site import (S3.7, story 5601). Pasted rows and an uploaded CSV file are both accepted, and
 * both go through this parser.
 *
 * The template's first six columns are fixed; the Region column and the five logistics columns are
 * appended after them, so a file exported from the older six-column template still imports. Rows
 * group by an exact site-name match: the first row for a name supplies its sub code, address, region
 * and logistics, and later rows with the same name contribute points of contact only.
 */

export const OFFICE_CSV_FILENAME = "tdda-site-template.csv";

export const OFFICE_CSV_HEADERS = [
  "Sub/Project Code",
  "Office/Site Name",
  "Address",
  "Contact Name",
  "Mobile",
  "Email",
  "Region",
  // The Imperans clinic servicing the site, by its name in the Imperans list (Epic 6303, story
  // 6373). Optional in the file: a row without one takes the default picked in the import dialog.
  "Servicing Clinic",
];

export const OFFICE_CSV_TEMPLATE = `${OFFICE_CSV_HEADERS.join(",")}
"AK-01","Head Office","1 Example Street, Suburb, City, 1234","Jane Example","021 555 0100","jane@example.co.nz","Auckland","Auckland Central"`;

export interface IOfficeCsvPointOfContact {
  fullName: string | null;
  mobile: string | null;
  emailAddress: string | null;
}

export interface IOfficeCsvRow {
  subCode: string | null;
  officeName: string;
  officeFullAddress: string;
  regionName: string | null;
  /** The Imperans clinic's name as typed in the file; resolved against the live list on import. */
  servicingClinicName: string | null;
  pointsOfContact: IOfficeCsvPointOfContact[];
}

export interface IOfficeCsvResult {
  rows: IOfficeCsvRow[];
  errors: string[];
  /** Rows dropped for carrying no site name — never a reason to abort the import (S3.7). */
  skipped: number;
}

/** Split one CSV line into fields, honouring double-quoted values so addresses keep their commas. */
export const parseCsvLine = (line: string): string[] => {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      fields.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  fields.push(current.trim());
  return fields;
};

/** A header row is recognised by its first column, whichever template version produced it. */
const isHeaderLine = (fields: string[]): boolean => {
  const first = (fields[0] || "").toLowerCase().replace(/[\s/]/g, "");
  return first === "subprojectcode" || first === "officesitename" || first === "officename";
};

const yesNo = (value: string | undefined): boolean =>
  ["yes", "y", "true", "1"].includes((value || "").trim().toLowerCase());

const blankToNull = (value: string | undefined): string | null => {
  const trimmed = (value || "").trim();
  return trimmed.length ? trimmed : null;
};

export const parseOfficesCsv = (text: string): IOfficeCsvResult => {
  const result: IOfficeCsvResult = { rows: [], errors: [], skipped: 0 };
  // The line number in an error has to match the physical file, blank separators included — a
  // filter-then-index approach numbered rows by their position among non-blank lines instead, so a
  // single blank line above the failing row already put every error message off by one (task 5848).
  // Blank lines are still skipped, just after each one has been paired with its real line number.
  const lines = (text || "")
    .split(/\r?\n/)
    .map((line, i) => ({ line: line.trim(), lineNumber: i + 1 }))
    .filter((x) => x.line.length > 0);

  // Keyed by the exact site name, so rows for one site group however they are ordered.
  const byName = new Map<string, IOfficeCsvRow>();

  lines.forEach(({ line, lineNumber }, index) => {
    const fields = parseCsvLine(line);
    if (index === 0 && isHeaderLine(fields)) return;

    const [
      subCode,
      officeName,
      address,
      contactName,
      mobile,
      email,
      regionName,
      // The Servicing Clinic column (story 6373). A file exported from the old template carried five
      // visit-logistics columns here instead; whatever lands in this slot is only ever matched
      // against the clinic list by name, so an old "Parking" value simply matches nothing and the
      // row takes the import's default clinic (task 5696's promise still holds).
      servicingClinicName,
    ] = fields;

    // A row with no site name is skipped without aborting the import (S3.7).
    if (!blankToNull(officeName)) {
      result.skipped += 1;
      return;
    }

    const name = officeName.trim();
    const pointOfContact =
      blankToNull(contactName) || blankToNull(mobile) || blankToNull(email)
        ? {
            fullName: blankToNull(contactName),
            mobile: blankToNull(mobile),
            emailAddress: blankToNull(email),
          }
        : null;

    const existing = byName.get(name);
    if (existing) {
      // Later rows for the same site contribute contacts only.
      if (pointOfContact) existing.pointsOfContact.push(pointOfContact);
      return;
    }

    // Every row is validated to the same rules as the manual form, and a failing row is reported
    // rather than silently stored — including the required address (S3.7).
    if (!blankToNull(address)) {
      result.errors.push(`Line ${lineNumber}: "${name}" needs an address.`);
      return;
    }

    const row: IOfficeCsvRow = {
      subCode: blankToNull(subCode)?.toUpperCase() ?? null,
      officeName: name,
      officeFullAddress: address.trim(),
      regionName: blankToNull(regionName),
      servicingClinicName: blankToNull(servicingClinicName),
      pointsOfContact: pointOfContact ? [pointOfContact] : [],
    };
    byName.set(name, row);
    result.rows.push(row);
  });

  return result;
};
